/**
 * Coût de revient d'un kit Piano LED, par période — saisi sur la page Frais.
 *
 * Une ligne = un coût (€ HT) du … au … (fin vide : toujours en cours). Chaque
 * commande prend le coût en vigueur à sa date : une marge de mars se calcule
 * au coût de mars. L'API refuse les chevauchements et dit laquelle gêne.
 */

import { KIT_PRODUCTION_COST_MAX, type KitCostPeriod, type KitCostPeriodInput } from '@shared/types/settings';
import { useEffect, useState, type FormEvent } from 'react';

import { useKitCosts, useWriteKitCost } from '../hooks/useKitCosts';
import { formatDayLong, formatMoney } from '../lib/format';
import { Card } from './Card';

interface Draft {
  startDate: string;
  endDate: string;
  cost: string;
  note: string;
}

const toDraft = (period?: KitCostPeriod): Draft =>
  period
    ? { startDate: period.startDate, endDate: period.endDate ?? '', cost: String(period.cost).replace('.', ','), note: period.note ?? '' }
    : { startDate: '', endDate: '', cost: '', note: '' };

function toInput(draft: Draft): KitCostPeriodInput | string {
  const cost = Number(draft.cost.replace(/\s/g, '').replace('€', '').replace(',', '.'));
  if (!draft.startDate) return 'Date de début requise.';
  if (draft.cost.trim() === '' || !Number.isFinite(cost) || cost < 0 || cost > KIT_PRODUCTION_COST_MAX) {
    return `Coût : un nombre entre 0 et ${KIT_PRODUCTION_COST_MAX} €.`;
  }
  if (draft.endDate && draft.endDate < draft.startDate) return 'La fin doit suivre le début.';
  return { startDate: draft.startDate, endDate: draft.endDate || null, cost, note: draft.note.trim() || null };
}

function PeriodRow({
  period,
  isSaving,
  onSave,
  onDelete,
  submitLabel,
}: {
  period?: KitCostPeriod;
  isSaving: boolean;
  onSave: (input: KitCostPeriodInput) => Promise<void>;
  onDelete?: () => Promise<void>;
  submitLabel: string;
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(period));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraft(toDraft(period)), [period]);

  const idPrefix = `kit-cost-${period?.id ?? 'new'}`;
  const dirty = !period || JSON.stringify(draft) !== JSON.stringify(toDraft(period));
  const field = (key: keyof Draft) => (event: { target: { value: string } }) =>
    setDraft((current) => ({ ...current, [key]: event.target.value }));

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const input = toInput(draft);
    if (typeof input === 'string') return setError(input);
    setError(null);
    try {
      await onSave(input);
      if (!period) setDraft(toDraft());
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Enregistrement impossible.');
    }
  };

  return (
    <form className="marker-form deduction-row" onSubmit={(event) => void handleSubmit(event)}>
      <div className="field">
        <label className="field__label" htmlFor={`${idPrefix}-start`}>Du</label>
        <input id={`${idPrefix}-start`} type="date" value={draft.startDate} onChange={field('startDate')} />
      </div>
      <div className="field">
        <label className="field__label" htmlFor={`${idPrefix}-end`}>Au (vide : en cours)</label>
        <input id={`${idPrefix}-end`} type="date" value={draft.endDate} onChange={field('endDate')} />
      </div>
      <div className="field">
        <label className="field__label" htmlFor={`${idPrefix}-cost`}>Coût € HT / kit</label>
        <input id={`${idPrefix}-cost`} type="text" inputMode="decimal" value={draft.cost} onChange={field('cost')} style={{ width: 90 }} placeholder="ex. 45" />
      </div>
      <div className="field marker-form__grow">
        <label className="field__label" htmlFor={`${idPrefix}-note`}>Note</label>
        <input id={`${idPrefix}-note`} type="text" maxLength={200} value={draft.note} onChange={field('note')} placeholder="ex. nouvelle série, boîtier v3" />
      </div>
      <div className="marker-form__actions">
        <button type="submit" className="btn btn--primary" disabled={isSaving || !dirty}>
          {submitLabel}
        </button>
        {onDelete && (
          <button type="button" className="btn" disabled={isSaving} onClick={() => void onDelete()}>
            Retirer
          </button>
        )}
      </div>
      {error && (
        <p className="login__error marker-form__error" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

export function KitCostPeriodsEditor() {
  const periods = useKitCosts();
  const write = useWriteKitCost();
  const isSaving = write.isPending;
  const current = (periods.data ?? []).find((period) => period.endDate === null);

  return (
    <Card
      title="Coût de revient d'un kit Piano LED"
      subtitle={`€ HT par kit, par période : chaque commande prend le coût en vigueur à sa date (COGS, marges, MC2). ${current ? `En cours : ${formatMoney(current.cost, 'EUR', true)} depuis le ${formatDayLong(current.startDate)}.` : 'Aucune période en cours : les kits récents sont sans coût.'}`}
      className="marker-form-card"
    >
      {periods.isError && (
        <p className="notice notice--error" role="alert">
          Coûts illisibles : {periods.error.message}
        </p>
      )}
      {(periods.data ?? []).map((period) => (
        <PeriodRow
          key={period.id}
          period={period}
          isSaving={isSaving}
          submitLabel="Enregistrer"
          onSave={async (input) => {
            await write.mutateAsync({ kind: 'update', id: period.id, input });
          }}
          onDelete={async () => {
            if (!window.confirm(`Retirer la période du ${formatDayLong(period.startDate)} (${formatMoney(period.cost, 'EUR', true)}) ?`)) return;
            await write.mutateAsync({ kind: 'delete', id: period.id });
          }}
        />
      ))}
      <PeriodRow
        isSaving={isSaving}
        submitLabel="Ajouter"
        onSave={async (input) => {
          await write.mutateAsync({ kind: 'create', input });
        }}
      />
    </Card>
  );
}
